import { invariantResponse } from '@epic-web/invariant'
import { data, NavLink, Outlet, useLoaderData } from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Badge, type Variant } from '#app/components/ui/badge'
import { Icon } from '#app/components/ui/icon.tsx'
import { Separator } from '#app/components/ui/separator'
import { prisma } from '#app/utils/db.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalAdminUser } from '#app/utils/user.ts'

type ScheduleState = 'pending' | 'open' | 'locked' | 'closed' | 'cancelled'

export async function loader() {
	const schedules = await prisma.schedule.findMany({
		select: {
			id: true,
			date: true,
			deadline: true,
			source: true,
			costPerHour: true,
			state: true,
		},
		orderBy: { date: 'desc' },
	})

	invariantResponse(schedules, 'No schedules found', { status: 404 })

	return data(schedules)
}

export default function SchedulesLayout() {
	const schedules = useLoaderData<typeof loader>()
	const canCreate = useOptionalAdminUser()
	const navLinkDefaultClassName =
		'line-clamp-2 block rounded-l-full flex flex-row flex-wrap py-2 pl-8 pr-6 text-base lg:text-xl'
	return (
		<div className="container mb-6 flex h-full px-0 pb-12 md:px-6">
			<div className="bg-muted grid min-h-[1000px] w-full grid-cols-8 pl-2 md:container md:rounded-3xl md:pr-0">
				<div className="relative col-span-2 mt-6">
					<div className="absolute inset-0 flex flex-col">
						<h1 className="text-center text-base font-bold md:text-lg lg:text-left lg:text-2xl">
							Schedules:
						</h1>
						<Separator className="mt-4 mb-2" />
						<ul className="overflow-x-hidden overflow-y-auto pb-12">
							<li key="active-schedule" className="p-1 pr-0">
								<NavLink
									to="active"
									preventScrollReset
									prefetch="intent"
									className={({ isActive }) =>
										cn(navLinkDefaultClassName, isActive && 'bg-accent')
									}
								>
									<Icon name="chevron-right">
										<em>{'Active Schedules'}</em>
									</Icon>
								</NavLink>
							</li>
							{canCreate ? (
								<li className="p-1 pr-0">
									<NavLink
										to="new"
										className={({ isActive }) =>
											cn(navLinkDefaultClassName, isActive && 'bg-accent')
										}
									>
										<Icon name="plus">New Schedule</Icon>
									</NavLink>
								</li>
							) : null}
							{schedules.map(({ id, date, state }) => (
								<li key={id} className="p-1 pr-0">
									<NavLink
										to={date}
										preventScrollReset
										prefetch="intent"
										className={({ isActive }) =>
											cn(navLinkDefaultClassName, isActive && 'bg-accent')
										}
									>
										<span className="min-w-32">{date}</span>
										<Badge
											className={getClassnamesForState(state as ScheduleState)}
											variant={getVariantForState(state as ScheduleState)}
										>
											{state}
										</Badge>
									</NavLink>
								</li>
							))}
						</ul>
					</div>
				</div>
				<div className="bg-accent relative col-span-6 md:rounded-r-3xl">
					<Outlet />
				</div>
			</div>
		</div>
	)
}

function getVariantForState(state: ScheduleState): Variant | undefined {
	const map: Partial<Record<ScheduleState, Variant>> = {
		pending: 'outline',
		open: 'default',
		locked: 'secondary',
		closed: 'destructive',
		cancelled: 'outline',
	}
	return map[state]
}

function getClassnamesForState(state: ScheduleState): string | undefined {
	const map: Partial<Record<ScheduleState, string>> = {
		pending: 'border-1 border-primary',
		cancelled: 'border-1 border-destructive text-destructive',
	}
	return cn('ml-2 capitalize', map[state])
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => <p>No Schedules found...</p>,
			}}
		/>
	)
}
